import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Heart, Star, Minus, Plus, ChevronLeft, Check, Truck, Shield } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useSEO } from '../hooks/useSEO';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${slug}`);
        setData(res.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const handleAddToCart = () => {
    if (data?.product) {
      addItem(data.product, quantity);
      toast.success(`${quantity} x ${data.product.name} aggiunto al carrello`);
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Accedi per aggiungere ai preferiti');
      return;
    }
    if (data?.producer) {
      try {
        if (isFavorite) {
          await api.delete(`/favorites/${data.producer.id}`);
          setIsFavorite(false);
          toast.success('Rimosso dai preferiti');
        } else {
          await api.post(`/favorites/${data.producer.id}`);
          setIsFavorite(true);
          toast.success('Aggiunto ai preferiti');
        }
      } catch (error) {
        toast.error('Errore nell\'aggiornamento dei preferiti');
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const _product = data?.product;
  const _producer = data?.producer;
  const _SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
  useSEO({
    title: _product?.name,
    description: _product?.description
      ? _product.description.slice(0, 155)
      : _product
        ? `Acquista ${_product.name} da ${_producer?.name || 'produttore del Sud Italia'}. Prodotto artigianale di qualità.`
        : undefined,
    image: _product?.image_url || undefined,
    canonicalPath: _product ? `/prodotti/${_product.slug}` : undefined,
    jsonLd: _product ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: _product.name,
      description: _product.description || undefined,
      image: _product.image_url || undefined,
      url: `${_SITE_URL}/prodotti/${_product.slug}`,
      sku: _product.id,
      brand: _producer ? { '@type': 'Brand', name: _producer.name } : undefined,
      offers: {
        '@type': 'Offer',
        price: _product.price,
        priceCurrency: 'EUR',
        availability: _product.inventory > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        seller: _producer ? { '@type': 'Organization', name: _producer.name } : undefined,
      },
      ...(data?.average_rating && data?.review_count > 0 ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: data.average_rating,
          reviewCount: data.review_count,
          bestRating: 5,
          worstRating: 1,
        },
      } : {}),
    } : undefined,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-32 mb-8" />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="aspect-square bg-gray-200 rounded-xl" />
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-10 bg-gray-200 rounded w-32" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary">Prodotto non trovato</h1>
          <Button asChild className="mt-4">
            <Link to="/prodotti">Torna ai prodotti</Link>
          </Button>
        </div>
      </div>
    );
  }

  const { product, producer, category, reviews, average_rating, review_count } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-8">
          <Link to="/prodotti" className="text-text-secondary hover:text-primary transition-colors flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" />
            Prodotti
          </Link>
          {category && (
            <>
              <span className="text-text-muted">/</span>
              <Link to={`/prodotti?category=${category.slug}`} className="text-text-secondary hover:text-primary transition-colors">
                {category.name}
              </Link>
            </>
          )}
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="space-y-4">
            <div className="aspect-square rounded-xl overflow-hidden bg-white border border-border">
              <img
                src={product.image_url || 'https://images.pexels.com/photos/7663192/pexels-photo-7663192.jpeg'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-24 lg:self-start space-y-6">
            {/* Producer */}
            {producer && (
              <Link 
                to={`/produttori/${producer.slug}`}
                className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-primary transition-colors"
                data-testid="product-producer-link"
              >
                {producer.logo_url && (
                  <img src={producer.logo_url} alt={producer.name} className="w-6 h-6 rounded-full object-cover" />
                )}
                <span>{producer.name}</span>
              </Link>
            )}

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
              {product.name}
            </h1>

            {/* Rating */}
            {average_rating && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(average_rating)
                          ? 'fill-accent text-accent'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-text-secondary">
                  {average_rating.toFixed(1)} ({review_count} recensioni)
                </span>
              </div>
            )}

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-primary">
                {formatPrice(product.price)}
              </span>
              {product.compare_price && product.compare_price > product.price && (
                <span className="text-lg text-text-muted line-through">
                  {formatPrice(product.compare_price)}
                </span>
              )}
              {product.unit && (
                <span className="text-sm text-text-secondary">/ {product.unit}</span>
              )}
            </div>

            {/* Availability */}
            <div className="flex items-center gap-2">
              {product.inventory > 0 ? (
                <>
                  <Check className="w-5 h-5 text-secondary" />
                  <span className="text-secondary font-medium">Disponibile</span>
                  <span className="text-text-muted">({product.inventory} in stock)</span>
                </>
              ) : (
                <span className="text-red-500 font-medium">Esaurito</span>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            {product.inventory > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-background transition-colors"
                    disabled={quantity <= 1}
                    data-testid="decrease-quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.inventory, quantity + 1))}
                    className="p-3 hover:bg-background transition-colors"
                    disabled={quantity >= product.inventory}
                    data-testid="increase-quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <Button size="lg" className="flex-1 gap-2" onClick={handleAddToCart} data-testid="add-to-cart-btn">
                  <ShoppingCart className="w-5 h-5" />
                  Aggiungi al Carrello
                </Button>

                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={handleToggleFavorite}
                  className={isFavorite ? 'text-red-500 border-red-500' : ''}
                  data-testid="favorite-btn"
                >
                  <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                </Button>
              </div>
            )}

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-secondary" />
                <div>
                  <p className="text-sm font-medium">Spedizione Diretta</p>
                  <p className="text-xs text-text-muted">Dal produttore a te</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-secondary" />
                <div>
                  <p className="text-sm font-medium">Qualità Garantita</p>
                  <p className="text-xs text-text-muted">Produttore verificato</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description & Story */}
        <div className="mt-12 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {product.description && (
              <div>
                <h2 className="font-heading text-2xl font-bold mb-4">Descrizione</h2>
                <p className="text-text-secondary whitespace-pre-line">{product.description}</p>
              </div>
            )}

            {product.story && (
              <div className="bg-white p-6 rounded-xl border border-border">
                <h2 className="font-heading text-2xl font-bold mb-4">La Storia</h2>
                <p className="text-text-secondary whitespace-pre-line">{product.story}</p>
              </div>
            )}

            {/* Reviews */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">
                Recensioni {review_count > 0 && `(${review_count})`}
              </h2>
              {reviews && reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-white p-4 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{review.user_name}</span>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= review.rating
                                  ? 'fill-accent text-accent'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-text-secondary text-sm">{review.comment}</p>
                      )}
                      <p className="text-xs text-text-muted mt-2">
                        {new Date(review.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-text-secondary">Nessuna recensione ancora.</p>
              )}
            </div>
          </div>

          {/* Producer Card */}
          {producer && (
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-xl border border-border sticky top-24">
                <h3 className="font-heading font-semibold mb-4">Il Produttore</h3>
                <Link to={`/produttori/${producer.slug}`} className="block">
                  <div className="flex items-center gap-4">
                    <img
                      src={producer.logo_url || 'https://images.pexels.com/photos/15899638/pexels-photo-15899638.jpeg'}
                      alt={producer.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-border"
                    />
                    <div>
                      <p className="font-semibold text-text-primary hover:text-primary transition-colors">
                        {producer.name}
                      </p>
                      <p className="text-sm text-text-secondary">Produttore Verificato</p>
                    </div>
                  </div>
                </Link>
                <Button variant="outline" asChild className="w-full mt-4">
                  <Link to={`/produttori/${producer.slug}`}>
                    Visita il Profilo
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
